import { create } from "zustand";

export interface AgentSpecStudioNavigationResource {
  id: string;
  title: string;
}

interface AgentSpecStudioStore {
  selectedThreadId: string | null;
  lastEventIdByThread: Record<string, number>;
  navigationAgentId: string | null;
  navigationResources: {
    workflows: AgentSpecStudioNavigationResource[];
    miniapps: AgentSpecStudioNavigationResource[];
  };
  /** Survives chat route remounts so pending turns keep polling after navigation. */
  scopedAgentKey: string | null;
  streaming: boolean;
  turnPending: boolean;
  pendingAfterSequence: number | null;
  error: string | null;
  setSelectedThreadId: (threadId: string | null) => void;
  setLastEventId: (threadId: string, eventId: number) => void;
  setNavigationResources: (
    agentId: string,
    resources: AgentSpecStudioStore["navigationResources"],
  ) => void;
  setStreaming: (streaming: boolean) => void;
  setTurnPending: (turnPending: boolean) => void;
  setPendingAfterSequence: (pendingAfterSequence: number | null) => void;
  setError: (error: string | null) => void;
  /**
   * Reset Studio chat UI state when the scoped Agent changes.
   * No-op on remount of the same org/agent (preserves turnPending across
   * navigation away from /studio/chat).
   */
  resetForAgent: (
    organizationId: string | null | undefined,
    agentId: string | null | undefined,
  ) => void;
}

function scopedKey(
  organizationId: string | null | undefined,
  agentId: string | null | undefined,
): string {
  return `${organizationId ?? ""}:${agentId ?? ""}`;
}

export const useAgentSpecStudioStore = create<AgentSpecStudioStore>((set) => ({
  selectedThreadId: null,
  lastEventIdByThread: {},
  navigationAgentId: null,
  navigationResources: { workflows: [], miniapps: [] },
  scopedAgentKey: null,
  streaming: false,
  turnPending: false,
  pendingAfterSequence: null,
  error: null,
  setSelectedThreadId: (selectedThreadId) =>
    set({ selectedThreadId, error: null }),
  setLastEventId: (threadId, eventId) =>
    set((state) => ({
      lastEventIdByThread: {
        ...state.lastEventIdByThread,
        [threadId]: eventId,
      },
    })),
  setNavigationResources: (navigationAgentId, navigationResources) =>
    set({ navigationAgentId, navigationResources }),
  setStreaming: (streaming) => set({ streaming }),
  setTurnPending: (turnPending) => set({ turnPending }),
  setPendingAfterSequence: (pendingAfterSequence) =>
    set({ pendingAfterSequence }),
  setError: (error) => set({ error }),
  resetForAgent: (organizationId, agentId) =>
    set((state) => {
      const nextKey = scopedKey(organizationId, agentId);
      if (state.scopedAgentKey === nextKey) {
        return state;
      }
      return {
        scopedAgentKey: nextKey,
        selectedThreadId: null,
        lastEventIdByThread: {},
        streaming: false,
        turnPending: false,
        pendingAfterSequence: null,
        error: null,
      };
    }),
}));
